import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, User, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CertMatchPills } from "@/components/CertMatch";
import { Loader, EmptyState, UserAvatar, SpecPill, AvailabilityPill, StatusPill, VipBadge } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { APP_STATUS, timeAgo } from "@/lib/labels";

function FileChip({ f }) {
  const api = useApi();
  const open = async () => {
    try {
      const blob = await api("get", `/files/${f.path}`, null, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = f.filename || "file"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button type="button" onClick={open} data-testid="applicant-attachment" className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded border border-slate-200 bg-white hover:border-sky-400">
      {f.content_type?.startsWith("image/") ? <ImageIcon size={13} /> : <FileText size={13} />}<span className="max-w-[180px] truncate">{f.filename}</span>
    </button>
  );
}

export default function JobApplicants() {
  const { id } = useParams();
  const api = useApi();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState(null);
  const [sort, setSort] = useState("match");
  const hasCerts = job?.required_certs?.length > 0;
  const sorted = useMemo(() => {
    if (!apps || !hasCerts || sort !== "match") return apps;
    const score = (a) => Object.values(a.cert_match || {}).filter(Boolean).length;
    return [...apps].sort((x, y) => score(y) - score(x) || (y.created_at > x.created_at ? 1 : -1));
  }, [apps, sort, hasCerts]);

  useEffect(() => {
    api("get", `/jobs/${id}`).then(setJob);
    api("get", `/jobs/${id}/applications`).then(setApps).catch(() => setApps([]));
  }, [id, api]);

  const setStatus = async (appId, status) => {
    try { await api("patch", `/applications/${appId}`, { status }); setApps(apps.map((a) => a.id === appId ? { ...a, status } : a)); toast.success("Status updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!apps) return <Loader />;
  return (
    <div>
      <Link to="/my-jobs" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="applicants-back-link"><ArrowLeft size={16} />My jobs</Link>
      <p className="eyebrow mt-6">Candidates</p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-3xl font-extrabold tracking-tight">{job?.title}</h1>
        {hasCerts && apps.length > 1 && (
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm" data-testid="applicants-sort">
            {[["match", "Best match"], ["newest", "Newest"]].map(([k, l]) => (
              <button key={k} onClick={() => setSort(k)} data-testid={`applicants-sort-${k}`}
                className={`px-3 py-1.5 rounded transition-colors ${sort === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-8 space-y-3">
        {apps.length === 0 ? <EmptyState testId="applicants-empty" title="No candidates yet" text="When a technician applies, you will see them here." /> : sorted.map((a) => (
          <div key={a.id} data-testid={`applicant-row-${a.id}`} className="panel p-5 flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="flex gap-4 flex-1 min-w-0">
              <UserAvatar user={a.technician} size={52} />
              <div className="min-w-0">
                <p className="font-bold flex items-center gap-2">{a.technician?.name}<VipBadge user={a.technician} /></p>
                <p className="text-sm text-slate-600">{a.technician?.headline}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(a.technician?.specializations || []).map((s) => <SpecPill key={s} spec={s} />)}
                  <AvailabilityPill value={a.technician?.availability} />
                  <span className="font-mono text-[11px] text-slate-400">{a.technician?.certificates?.length || 0} certificate{a.technician?.certificates?.length === 1 ? "" : "s"} · {timeAgo(a.created_at)}</span>
                </div>
                {a.cert_match && Object.keys(a.cert_match).length > 0 && (
                  <div className="mt-3"><p className="eyebrow mb-1.5">Required certificates · {Object.values(a.cert_match).filter(Boolean).length}/{Object.keys(a.cert_match).length}</p>
                    <CertMatchPills certs={Object.keys(a.cert_match)} match={a.cert_match} testId={`applicant-certs-${a.id}`} /></div>
                )}
                {a.message && <p className="mt-3 text-sm text-slate-700 bg-slate-50 border-l-2 border-amber-400 px-3 py-2 whitespace-pre-line">{a.message}</p>}
                {(a.attachments || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`applicant-attachments-${a.id}`}>
                    {a.attachments.map((f) => <FileChip key={f.path} f={f} />)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <StatusPill status={a.status} testId={`applicant-status-${a.id}`} />
              <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                <SelectTrigger className="w-[160px] h-9" data-testid={`applicant-status-select-${a.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(APP_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button asChild size="sm" variant="outline" data-testid={`applicant-profile-${a.id}`}><Link to={`/u/${a.technician_id}`}><User size={14} />Profile</Link></Button>
              <Button asChild size="sm" data-testid={`applicant-message-${a.id}`}><Link to={`/messages/${a.technician_id}`}><MessageSquare size={14} />Message</Link></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
